import React, { useEffect, useState } from "react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getAccountHealth, AccountHealthScore } from '@/lib/accountIntegrity';
import { useSession } from '@/hooks/useSession';

export default function ReadinessGauge() {
  const { user } = useSession();
  const [health, setHealth] = useState<AccountHealthScore | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    async function load() {
      if (!user) return;
      const h = await getAccountHealth(user.id);
      setHealth(h);
    }
    load();
  }, [user]);

  const score = health?.healthScore ?? health?.score ?? 0;
  const normalized = Math.min(100, Math.max(0, score ?? 0));

  const getColor = (s: number) => {
    if (s >= 80) return 'bg-green-400';
    if (s >= 50) return 'bg-yellow-400';
    return 'bg-blue-300';
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <HoverCard>
        <HoverCardTrigger asChild>
          <button
            onClick={() => setOpen(true)}
            className="relative flex items-center justify-center rounded-full w-20 h-20 bg-card ring-1 ring-border shadow-sm"
            aria-label="Open readiness details"
          >
            <div className={`p-1 rounded-full ${getColor(normalized)}`}>
              {/* Simple circular gauge using SVG (stroke uses currentColor) */}
              <svg viewBox="0 0 36 36" className="w-14 h-14 text-white">
                <path d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="4" className="opacity-20" />
                <path
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  fill="none"
                  strokeDasharray={`${normalized} ${100 - normalized}`}
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                  transform="rotate(-90 18 18)"
                  style={{ transition: 'stroke-dasharray 400ms ease' }}
                />
                <text x="18" y="20" textAnchor="middle" className="text-sm font-semibold fill-white" fontSize="6">{normalized}</text>
              </svg>
            </div>
          </button>
        </HoverCardTrigger>

        <HoverCardContent side="bottom" className="w-64 p-3 bg-card border-border shadow-2xl">
          <div className="text-sm font-medium">What affects your readiness</div>
          <div className="mt-2 text-xs space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Verification</span>
              <span className="font-medium">{health?.verificationScore ?? '--'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Trusted devices</span>
              <span className="font-medium">{health?.deviceScore ?? '--'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Session safety</span>
              <span className="font-medium">{health?.sessionScore ?? '--'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Anomaly signals</span>
              <span className="font-medium">{health?.anomalyScore ?? '--'}</span>
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>

      <div className="text-xs text-muted-foreground">{health ? (health.healthBand ? health.healthBand.toUpperCase() : (normalized >= 80 ? 'HIGH' : normalized >= 50 ? 'MEDIUM' : 'LOW')) : 'Unknown'}</div>

      {/* Dialog for click (detailed overlay) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Your continuity readiness</DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">A breakdown of factors that affect this score. Institutions only see shared facts when needed.</p>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">Readiness Score</div>
                <div className="text-2xl font-semibold">{normalized} / 100</div>
              </div>
              <div className={`px-2 py-1 rounded ${getColor(normalized)} text-white text-sm`}>
                {normalized >= 80 ? 'High' : normalized >= 50 ? 'Medium' : 'Low'}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border p-3">
                <div className="text-xs text-muted-foreground">Verification</div>
                <div className="font-medium mt-1">{health?.verificationScore ?? '--'}</div>
                <div className="text-[12px] text-muted-foreground mt-1">Confirmed IDs, email, phone, KYC</div>
              </div>

              <div className="rounded-lg border border-border p-3">
                <div className="text-xs text-muted-foreground">Trusted devices</div>
                <div className="font-medium mt-1">{health?.deviceScore ?? '--'}</div>
                <div className="text-[12px] text-muted-foreground mt-1">Active, verified devices and passkeys</div>
              </div>

              <div className="rounded-lg border border-border p-3">
                <div className="text-xs text-muted-foreground">Session safety</div>
                <div className="font-medium mt-1">{health?.sessionScore ?? '--'}</div>
                <div className="text-[12px] text-muted-foreground mt-1">Concurrent sessions and session hygiene</div>
              </div>

              <div className="rounded-lg border border-border p-3">
                <div className="text-xs text-muted-foreground">Anomalies</div>
                <div className="font-medium mt-1">{health?.anomalyScore ?? '--'}</div>
                <div className="text-[12px] text-muted-foreground mt-1">Recent suspicious events</div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground italic">Tip: Improve verification and trusted device counts to raise your readiness. Changes are deliberate and reversible.</p>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Close</Button>
              <Button size="sm" onClick={() => { setOpen(false); window.location.href = '/app/authority-state'; }}>View authority</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
